import React, { useState } from "react";
import StatCards from "../StatCards/StatCards";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataTable } from "../table/Table";
import { useCaseContext } from "../../Contexts/CaseContext";
import { CaseItem } from "../../Contexts/types";
import PageTop from "../page-top/PageTop";
import { useUserContext } from "../../Contexts/UserContext";
import { message, Modal } from "antd";
import PersonForm from "./VCApplication/Form";
import { FormState } from "./VCApplication/VcApplication";

const tabs = [
	{ tab: "all-cases", index: 0 },
	{ tab: "pending-cases", index: 1 },
	{ tab: "rejected-cases", index: 2 },
	{ tab: "approved-cases", index: 3 },
] as const;

const AllCases: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const tabFilter = searchParams.get("tab");
	const [tabIndex, setTabIndex] = useState(
		() => tabs.find((t) => t.tab === tabFilter)?.index || 0
	);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [tabId, setTabId] = useState(
		() => tabs.find((t) => t.tab === tabFilter)?.index || 0
	);
	const [activeTab, setActiveTab] = useState<Tab>(
		() => tabs.find((t) => t.tab === tabFilter)?.tab || "all-cases"
	);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
	const [selectedCaseTitle, setSelectedCaseTitle] = useState<string | null>(
		null
	);
	const { caseData } = useCaseContext();
	const [searchQuery, setSearchQuery] = useState("");
	const [WitnessForms, setWitnessForms] = useState<FormState[]>([
		{
			firstName: "",
			lastName: "",
			middleName: "",
			email: "",
			mobile: "",
			address: "",
		},
	]);
	const [defendantCounselId, setDefendantCounselId] = useState<string | null>(
		null
	);
	const [claimantCounselId, setClaimantCounselId] = useState<string | null>(
		null
	);

	const navigate = useNavigate();


	const countCasesByStatus = (cases: CaseItem[], status: string): number => {
		return cases.filter((caseItem) => caseItem.basicData.status === status)
			.length;
	};
	const { userData } = useUserContext();
	const rejectedCount = countCasesByStatus(caseData, "REJECTED");
	const approvedCount = countCasesByStatus(caseData, "APPROVED");
	const pendingCount = countCasesByStatus(caseData, "PENDING");

	const leaveCase = async (suitNumber, counselType) => {
		const authToken = sessionStorage.getItem("token");
		console.log(suitNumber, counselType);
		try {
			const response = await fetch(
				"https://backend-kyw8.onrender.com/cases/drop",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${authToken}`,
					},
					body: JSON.stringify({ suitNumber, counselType }),
				}
			);
			const data = response.json();
			message.error("left case");
			console.log(data);
		} catch (error) {
			message.error("error leaving case. try later");
		}
	};

	const showConfirmationModal = (id, counselType) => {
		console.log(id, counselType);
		Modal.confirm({
			title: `Are you sure you want to leave this case?`,
			content: `This action will remove you as a counsel from the case.`,
			okText: "Yes",
			cancelText: "No",
			onOk: () => {
				leaveCase(id, counselType);
			},
		});
	};

	function handleTabChange(idx: number) {
		setActiveTab(tabs[idx].tab);
		setTabId(tabs[idx].index);
		setTabIndex(idx);
		const activeTab = tabs[idx].tab;

		// Update URL query
		setSearchParams((searchParams) => {
			searchParams.set("tab", activeTab);
			return searchParams;
		});
	}

	function handleSearch(search: string) {
		setSearchQuery(search);
	}

	const showAddWitnessModal = (id: string, title: string) => {
		console.log(id);
		setSelectedCaseId(id);
		setSelectedCaseTitle(title);
		setIsModalVisible(true);
	};

	const handleOk = async () => {
		console.log(`Case ID: ${selectedCaseId}`);
		console.log(WitnessForms);

		const authToken = sessionStorage.getItem("token");
		console.log(authToken);
		let formData;

		// Determine which witness forms to include
		if (userData.id === claimantCounselId) {
			formData = { claimantWitnesses: [...WitnessForms] };
		} else if (userData.id === defendantCounselId) {
			formData = { defendantWitnesses: [...WitnessForms] };
		} else {
			console.error("User is not authorized to submit this form.");
			message.error("You are not authorized to perform this action.");
			return;
		}

		console.log({ formData });

		try {
			const response = await fetch(
				`https://backend-kyw8.onrender.com/cases/${selectedCaseId}`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${authToken}`,
					},
					body: JSON.stringify(formData),
				}
			);

			const data = await response.json();

			if (response.ok) {
				message.success(data.message || "Form submitted successfully.");
			} else {
				message.error(data.error || "Failed to submit the form.");
			}

			console.log("Response Data:", data);
		} catch (error: any) {
			console.error("Error submitting form:", error);
			message.error(
				"An error occurred while submitting the form. Please try again."
			);
		}

		setIsModalVisible(false);
	};

	const handleCancel = () => {
		setIsModalVisible(false);
	};

	function getFilteredCases() {
		const lowercasedQuery = searchQuery.toLowerCase();
		return caseData.filter(
			(caseItem: CaseItem) =>
				Object.values(caseItem.basicData).some((value) =>
					value?.toString().toLowerCase().includes(lowercasedQuery)
				) ||
				Object.values(caseItem.caseType).some((value) =>
					value?.toString().toLowerCase().includes(lowercasedQuery)
				) ||
				Object.values(caseItem.courtBasicData).some((value) =>
					value?.toString().toLowerCase().includes(lowercasedQuery)
				)
		);
	}

	function getTableData() {
		const filteredCases = getFilteredCases();
		switch (activeTab) {
			case "pending-cases":
				return filteredCases.filter(
					(caseItem: CaseItem) => caseItem.basicData.status === "PENDING"
				);
			case "approved-cases":
				return filteredCases.filter(
					(caseItem: CaseItem) => caseItem.basicData.status === "APPROVED"
				);
			case "rejected-cases":
				return filteredCases.filter(
					(caseItem: CaseItem) => caseItem.basicData.status === "REJECTED"
				);
			default:
				return filteredCases;
		}
	}

	function getTableMenuOptions() {
		if (
			userData.userRole === "JUDGE" ||
			userData.userRole === "LEGAL_REGISTRAR"
		) {
			return [
				{
					text: "Update Case Status",
					onClick: (caseItem: CaseItem) => {
						// Handle update case status
						navigate(`/dashboard/edit-case-details/${caseItem.basicData.id}`, {
							state: { id: caseItem.basicData.id },
						});
					},
				},
				{
					text: "View Case",
					onClick: (caseItem: CaseItem) => {
						navigate(`/dashboard/case-details/${caseItem.basicData.id}`, {
							state: { id: caseItem.basicData.id },
						});
					},
				},
			];
		} else if (userData.userRole === "TRANSCRIBER") {
			return [
				{
					text: "View Case",
					onClick: (caseItem: CaseItem) => {
						navigate(`/dashboard/case-details/${caseItem.basicData.id}`, {
							state: { id: caseItem.basicData.id },
						});
					},
				},
				{
					text: "View/Edit Transcription",
					onClick: (caseItem: CaseItem) => {
						navigate(`/dashboard/view-transcription/${caseItem.basicData.id}`, {
							state: { id: caseItem.basicData.id },
						});
					},
				},
			];
		} else if (userData.userRole === "COURT_REGISTRAR") {
			return [
				{
					text: "View Case",
					onClick: (caseItem: CaseItem) => {
						navigate(`/dashboard/case-details/${caseItem.basicData.id}`, {
							state: { id: caseItem.basicData.id },
						});
					},
				},
				{
					text: "Upload Case Documents",
					onClick: (caseItem: CaseItem) => {
						navigate(
							`/dashboard/upload-more-document/${caseItem.basicData.id}`,
							{ state: { id: caseItem.basicData.id } }
						);
					},
				},
			];
		} else if (userData.userRole === "COUNSEL") {
			return [
				{
					text: "View Case",
					onClick: (caseItem: CaseItem) => {
						navigate(`/dashboard/case-details/${caseItem.basicData.id}`, {
							state: { id: caseItem.basicData.id },
						});
					},
				},
				{
					text: "Upload new Documents",
					onClick: (caseItem: CaseItem) => {
						navigate(
							`/dashboard/upload-more-document/${caseItem.basicData.id}`,
							{ state: { id: caseItem.basicData.id } }
						);
					},
				},
				{
					text: "Add Witness",
					onClick: (caseItem: CaseItem) => {
						showAddWitnessModal(
							caseItem.basicData.id,
							caseItem.basicData.title
						);
						setClaimantCounselId(caseItem.claimantCounsels[0]?.id);
						setDefendantCounselId(caseItem.defendantCounsels[0]?.id);
						console.log(claimantCounselId);
						console.log(defendantCounselId);
					},
				},
				{
					text: "Leave Case",
					onClick: (caseItem: CaseItem) => {
						showConfirmationModal(
							caseItem.basicData.suitNumber,
							userData.id === caseItem.claimantCounsels[0].id ? "claimant" : "defendant"
						  );
					},
				},
			];
		}
		return [];
	}

	const tableData = getTableData();
	const tableMenuOptions = getTableMenuOptions();

	return (
		<div>
			<div>
				<StatCards
					summary={[
						{
							name: "All Cases",
							value: caseData.length,
						},
						{
							name: "Pending Cases",
							value: pendingCount,
						},
						{
							name: "Rejected Cases",
							value: rejectedCount,
						},
						{
							name: "Approved Cases",
							value: approvedCount,
						},
					]}
					showLoader={false}
					activeTabIndex={tabIndex}
					onTabChange={handleTabChange}
					isTab
				/>
			</div>

			<PageTop header="Case Management" onSearch={handleSearch} />

			<div className="content-padding">
				<DataTable
					columns={[
						{ accessorKey: "basicData.suitNumber", label: "SUIT NUMBER" },
						{ accessorKey: "basicData.title", label: "PARTIES" },
						{ accessorKey: "caseType.title", label: "CLASSIFICATION" },
						{ accessorKey: "judge.fullName", label: "JUDGE NAME" },
						{
							accessorKey: "basicData.nextSittingDate",
							label: "NEXT ADJOURNED DATE",
							cell: ({ row }) => {
								const date = row.original.basicData.nextSittingDate;
								return (
									<span>
										{date
											? new Date(date).toLocaleDateString()
											: "No Date Available"}
									</span>
								);
							},
						},
						{ accessorKey: "courtBasicData.title", label: "COURT ROOM NUMBER" },
						{ accessorKey: "basicData.status", label: "STATUS" },
						{ accessorKey: "action", label: "" },
					]}
					data={tableData}
					subMenuOptions={tableMenuOptions}
				/>
			</div>
			<div>
				<Modal
					title={`Add witness for ${selectedCaseTitle}`}
					open={isModalVisible}
					onOk={handleOk}
					onCancel={handleCancel}
					style={{
						maxHeight: "500px",
						overflowY: "auto",
					}}>
					<PersonForm
						forms={WitnessForms}
						setForms={setWitnessForms}
						title="Witness"
					/>
				</Modal>
			</div>
		</div>
	);
};

type Tab = "all-cases" | "pending-cases" | "rejected-cases" | "approved-cases";

export default AllCases;
